import { Flex, Radio, Text } from '@mantine/core';

import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { LoanDefinitionType, SecurityType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { VestingTermsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/VestingTermsDrawer';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { useInvestmentTable } from './useInvestmentTable';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';

interface Props {
	getInvestmentReference: (
		investmentWorkflowType: string,
		isReleased: boolean,
		date: string
	) => string;
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const InvestmentTable = ({ getInvestmentReference, getSecurityTypeTranslation }: Props) => {
	const {
		investorDetailsData,
		data,
		columns,
		vestingTerms,
		onVestingTermApply,
		onVestingTermRemove,
		onVestingStartDateChange,
		vestingTermUpdateLoading,
		tMipManage,
		selectedInvestment,
		setSelectedInvestment,
		view,
		setView,
		showViewSwitch,
		revokeDrawerOpened,
		onRevokeDrawerClose,
		onRevokeConfirm,
		revokeLoading,
		revokeSuccess,
		investmentsLoading,
		dataOperations,
	} = useInvestmentTable({
		getInvestmentReference,
		getSecurityTypeTranslation,
	});

	return (
		<div
			style={{ position: 'relative' }}
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_INVESTMENT_TABLE}
		>
			<Text fz="lg" fw={800} mt="2rem" mb="1rem">
				{tMipManage('investors.expanded_row.investments')}
			</Text>

			{showViewSwitch && (
				<Radio.Group value={view} onChange={(value: typeof view) => setView(value)}>
					<Flex gap="1rem" mb="1rem">
						<Radio
							value="completed"
							label={tMipManage('investors.details.completed')}
						/>
						<Radio value="pending" label={tMipManage('investors.details.pending')} />
					</Flex>
				</Radio.Group>
			)}

			{data?.length || investmentsLoading ? (
				<MantineTable
					data={data}
					columns={columns}
					getRowId={(row) => row.investmentId}
					dataOperations={dataOperations}
					manualSorting={false}
					enablePagination={false}
					enableSearchBar={false}
					enableTopToolbar={false}
					enableRowSelection={false}
					enableFilters={false}
					enableHiding={false}
					isLoading={investmentsLoading}
					mantineTableBodyRowProps={({ row }) => ({
						id: row.id,
						style: {
							height: 'unset',
						},
					})}
				/>
			) : (
				<Flex maw="60rem">
					<InfoBox
						iconVariant="information"
						alignContent="center"
						title={tMipManage('investors.details.alerts.investment_title')}
						description={tMipManage('investors.details.alerts.investment_description')}
					/>
				</Flex>
			)}

			<VestingTermsDrawer
				opened={!!selectedInvestment}
				onClose={() => {
					setSelectedInvestment(undefined);
				}}
				vestingTerms={vestingTerms || []}
				selectedInvestment={selectedInvestment}
				onVestingTermApply={onVestingTermApply}
				onVestingTermRemove={onVestingTermRemove}
				onVestingDateChange={onVestingStartDateChange}
				getInvestmentReference={getInvestmentReference}
				getSecurityTypeTranslation={getSecurityTypeTranslation}
				loading={vestingTermUpdateLoading}
				currency={investorDetailsData?.currency}
			/>

			<ConfirmDeleteDrawer
				opened={revokeDrawerOpened}
				onClose={onRevokeDrawerClose}
				title={tMipManage('investors.expanded_row.revoke_drawer.title')}
				promptText={tMipManage('investors.expanded_row.revoke_drawer.promptText')}
				confirmDeleteText={tMipManage(
					'investors.expanded_row.revoke_drawer.confirmDeleteText'
				)}
				inputLabel={tMipManage('investors.expanded_row.revoke_drawer.inputLabel')}
				inputPlaceholder={tMipManage(
					'investors.expanded_row.revoke_drawer.inputPlaceholder'
				)}
				wordMismatchErrorMessage={tMipManage(
					'investors.expanded_row.revoke_drawer.wordMismatchErrorMessage'
				)}
				deleteBtnText={tMipManage('investors.expanded_row.revoke_drawer.deleteBtnText')}
				cancelBtnText={tMipManage('investors.expanded_row.revoke_drawer.cancelBtnText')}
				handleDelete={onRevokeConfirm}
				deleteSuccess={revokeSuccess}
				deleteLoading={revokeLoading}
				enableDeleteButtonOnlyOnConfirmDelete
			/>
		</div>
	);
};
